import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HiSearch } from "react-icons/hi";
import LayoutBuyerSeller from "../../components/layout/LayoutBuyerSeller";
import HomeIcon from "../../assets/icon/home.svg";
import carIcon from "../../assets/icon/car.svg";
import bagIcon from "../../assets/icon/bag.svg";
import watchIcon from "../../assets/icon/watch.svg";
import CreateListingModal from "../../components/models/CreateListingModal";
import apiService from "../../actions/apiService";
import TransactionManagementModal from "../../components/models/TransactionManagementModal";
import ConfirmTransactionDoneModal from "../../components/models/ConfirmTransactionDoneModal";
import { getListingStatus } from "../../utils/listingStatusUtils";

const CATEGORY_CHIPS = [
  { key: "all", label: "All categories", icon: "" },
  { key: "cars", label: "Cars", icon: carIcon },
  { key: "bags", label: "Bags", icon: bagIcon },
  { key: "watches", label: "Watches", icon: watchIcon },
  { key: "real_estate", label: "Real Estate", icon: HomeIcon },
  { key: "other", label: "Other", icon: "" },
];

// Map API category keys to their icon assets for reuse in grid/table views
const CATEGORY_ICON_BY_KEY = {
  cars: carIcon,
  bags: bagIcon,
  watches: watchIcon,
  real_estate: HomeIcon,
};

const SORT_OPTIONS = [
  { key: "recent", label: "Most recent" },
  { key: "price_low_high", label: "Price: Low to High" },
  { key: "price_high_low", label: "Price: High to Low" },
];

// Removed MOCK_LISTINGS; using fetched data only

// Utility function to truncate text
const truncateText = (text, maxLength = 25) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const SellerMylistings = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("recent");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'table'
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [listings, setListings] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [hasBankDetails, setHasBankDetails] = useState(null);
  const [checkingBank, setCheckingBank] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [selectedTransactionItem, setSelectedTransactionItem] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedConfirmListing, setSelectedConfirmListing] = useState(null);
  const [confirmingTransaction, setConfirmingTransaction] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  // persist view preference similar to reference UI behavior
  useEffect(() => {
    const v = localStorage.getItem("seller_listings_view");
    if (v === "grid" || v === "table") setViewMode(v);
  }, []);

  // Check bank account existence for current user
  useEffect(() => {
    const checkBank = async () => {
      setCheckingBank(true);
      try {
        const res = await apiService.get("/bank-accounts/check");
        setHasBankDetails(!!res?.data?.exists);
      } catch (e) {
        setHasBankDetails(false);
      } finally {
        setCheckingBank(false);
      }
    };
    checkBank();
  }, []);
  useEffect(() => {
    localStorage.setItem("seller_listings_view", viewMode);
  }, [viewMode]);

  // Build query params memoized from UI filters
  const queryParams = useMemo(() => {
    const params = { page: 1, limit: 100 };

    // map category chip to backend "type"
    const typeMap = {
      cars: "car",
      bags: "bag",
      watches: "watch",
      real_estate: "real_estate",
      other: "other",
    };
    if (category in typeMap) params.type = typeMap[category];

    // map status filter value -> backend item.status
    // UI values: 'active', 'pending', 'sold'
    const statusMap = {
      active: "available",
      pending: "pending",
      sold: "sold",
    };
    if (status in statusMap) {
      params.status = statusMap[status];
    }

    // search
    if (search && search.trim().length > 0) params.search = search.trim();

    // sort + order
    if (sort === "recent") {
      params.sort = "created_at";
      params.order = "desc";
    } else if (sort === "price_low_high") {
      params.sort = "price";
      params.order = "asc";
    } else if (sort === "price_high_low") {
      params.sort = "price";
      params.order = "desc";
    }

    return params;
  }, [category, status, search, sort]);

  // Fetch items only after bank details existence is confirmed
  useEffect(() => {
    let active = true;
    if (hasBankDetails !== true) {
      return;
    }
    const fetchItems = async () => {
      setIsFetching(true);
      setFetchError(null);
      try {
        const params = queryParams;
        const itemsResult = await apiService.get("/items", params);

        if (!active) return;

        if (itemsResult.success) {
          const items = (itemsResult.data?.items || itemsResult.data || []).map(
            (item) => {
              const primaryImage = Array.isArray(item.images)
                ? item.images.find((img) => img.is_primary) || item.images[0]
                : null;

              // determine released transaction to compute escrow amount
              const releasedTransaction = Array.isArray(item.transactions)
                ? item.transactions.find(
                    (tx) => tx?.status?.toLowerCase() === "released"
                  )
                : null;
              const escrowAmount = releasedTransaction
                ? Number(releasedTransaction.seller_payout) || 0
                : 0;

              // Get listing status based on flowchart
              const listingStatus = getListingStatus(item);

              // map backend -> UI
              const categoryMap = {
                car: "cars",
                bag: "bags",
                watch: "watches",
                real_estate: "real_estate",
                other: "other",
              };
              // backend item.status -> UI status filter values
              const statusMapBackToUi = {
                available: "active",
                pending: "pending",
                sold: "sold",
              };
              // Prefer accepted purchase request amount if available
              const purchaseRequests =
                item?.purchase_requests || item?.purchaseRequests || [];
              const acceptedRequest = Array.isArray(purchaseRequests)
                ? purchaseRequests.find(
                    (pr) => pr?.status?.toLowerCase() === "accepted"
                  )
                : null;
              const offerPrice = Number(acceptedRequest?.offer_amount);
              const resolvedPrice = Number.isFinite(offerPrice)
                ? offerPrice
                : Number(item.price);

              return {
                id: item.id,
                title: item.title,
                price: Number.isFinite(resolvedPrice)
                  ? resolvedPrice
                  : Number(item.price) || 0,
                currency: item.currency || "EUR",
                // any unknown status (including old 'draft') treated as 'active'
                status: statusMapBackToUi[item.status] || "active",
                category: categoryMap[item.category] || item.category || "bags",
                image: primaryImage?.file_url || "/static/img/bag-sample.jpg",
                escrowAmount,
                listingStatus,
                activeTransaction: item.transactions?.[0] || null,
              };
            }
          );
          setListings(items);
        } else {
          setFetchError(itemsResult.error || "Failed to load items");
          setListings([]);
        }
      } catch (e) {
        setFetchError("Failed to load items");
        setListings([]);
      } finally {
        if (active) setIsFetching(false);
      }
    };

    const handle = setTimeout(fetchItems, search ? 350 : 0);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [queryParams, search, hasBankDetails]);

  // Function to create new listing using API service
  const handleCreateListing = async (listingData) => {
    setIsLoading(true);
    setError(null);

    // Debug: log received data
    console.log("handleCreateListing - Received listingData:", listingData);
    console.log("handleCreateListing - Location value:", listingData.location);

    try {
      // Create FormData object to match the cURL format
      const formData = new FormData();

      // Add basic fields
      formData.append("title", listingData.title);
      formData.append("category", listingData.category);
      if (listingData.category === "other" && listingData.customCategory) {
        formData.append("custom_category", listingData.customCategory);
      }
      formData.append("description", listingData.description);
      formData.append("price", listingData.price.toString());
      formData.append("location", listingData.location || "");

      // Always append plate_number when category is car (even if empty) so backend can validate
      if (listingData.category === "car") {
        formData.append("plate_number", listingData.plateNumber || "");
      } else if (listingData.plateNumber) {
        formData.append("plate_number", listingData.plateNumber);
      }

      // Add custom properties as JSON string
      if (
        listingData.additionalProperties &&
        listingData.additionalProperties.length > 0
      ) {
        const customProps = {};
        listingData.additionalProperties.forEach((prop) => {
          customProps[prop.name] = prop.value;
        });
        formData.append("custom_properties", JSON.stringify(customProps));
      }

      // Add images
      if (listingData.images && listingData.images.length > 0) {
        listingData.images.forEach((image) => {
          formData.append("images", image);
        });
      }

      // Call the API service with FormData
      const result = await apiService.postFormData("/items", formData);

      if (result.success) {
        // Success - close modal and refresh listings
        setIsCreateOpen(false);
        // You can add a success message here
        // Add the new item to the local list so no reload is needed
        const rawItem = result?.data?.item || result?.data;
        if (rawItem) {
          const primaryImage = Array.isArray(rawItem.images)
            ? rawItem.images.find((img) => img.is_primary) || rawItem.images[0]
            : null;
          const categoryMap = {
            car: "cars",
            bag: "bags",
            watch: "watches",
            real_estate: "real_estate",
            other: "other",
          };
          // backend item.status -> UI status filter values
          // UI statuses: Active, Pending, Sold
          const statusMap = {
            available: "active",
            pending: "pending",
            sold: "sold",
          };
          // Prefer accepted purchase request amount if available
          const purchaseRequests =
            rawItem?.purchase_requests || rawItem?.purchaseRequests || [];
          const acceptedRequest = Array.isArray(purchaseRequests)
            ? purchaseRequests.find(
                (pr) => pr?.status?.toLowerCase() === "accepted"
              )
            : null;
          const offerPrice = Number(acceptedRequest?.offer_amount);
          const resolvedPrice = Number.isFinite(offerPrice)
            ? offerPrice
            : Number(rawItem.price);

          const mapped = {
            id: rawItem.id,
            title: rawItem.title,
            price: Number.isFinite(resolvedPrice)
              ? resolvedPrice
              : Number(rawItem.price) || 0,
            currency: rawItem.currency || "EUR",
            // any unknown status is treated as 'active'
            status: statusMap[rawItem.status] || "active",
            category:
              categoryMap[rawItem.category] || rawItem.category || "bags",
            image: primaryImage?.file_url || "",
            escrowAmount: 0,
            listingStatus: getListingStatus(rawItem),
            activeTransaction: rawItem.transactions?.[0] || null,
          };
          setListings((prev) => [mapped, ...prev]);
          // Open Transaction Management Modal with the newly created item
          setSelectedItemId(rawItem.id);
          setIsTransactionModalOpen(true);
        }
      } else {
        // API returned an error
        setError(result.error || "Failed to create listing");
      }
    } catch (err) {
      // Network or other error
      setError("Network error. Please try again.");
      console.error("Create listing error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const IconGrid = ({ className = "h-4 w-4" }) => (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path d="M3 3h4v4H3V3zm0 6h4v4H3V9zm0 6h4v4H3v-4zM9 3h4v4H9V3zm0 6h4v4H9V9zm0 6h4v4H9v-4zM15 3h4v4h-4V3zm0 6h4v4h-4V9zm0 6h4v4h-4v-4z" />
    </svg>
  );
  const IconList = ({ className = "h-4 w-4" }) => (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path d="M2 4h3v3H2V4zm5 0h11v3H7V4zM2 9h3v3H2V9zm5 0h11v3H7V9zm-5 5h3v3H2v-3zm5 0h11v3H7v-3z" />
    </svg>
  );

  // Results already filtered by backend
  const filtered = useMemo(() => {
    return Array.isArray(listings) ? listings : [];
  }, [listings]);

  // Refresh listings after action
  const refreshListings = async () => {
    const params = queryParams;
    setIsFetching(true);
    try {
      const itemsResult = await apiService.get("/items", params);

      if (itemsResult.success) {
        const items = (itemsResult.data?.items || itemsResult.data || []).map(
          (item) => {
            const primaryImage = Array.isArray(item.images)
              ? item.images.find((img) => img.is_primary) || item.images[0]
              : null;
            const releasedTransaction = Array.isArray(item.transactions)
              ? item.transactions.find(
                  (tx) => tx?.status?.toLowerCase() === "released"
                )
              : null;
            const escrowAmount = releasedTransaction
              ? Number(releasedTransaction.seller_payout) || 0
              : 0;

            // Get listing status based on flowchart
            const listingStatus = getListingStatus(item);

            const categoryMap = {
              car: "cars",
              bag: "bags",
              watch: "watches",
              real_estate: "real_estate",
              other: "other",
            };
            const statusMapBackToUi = {
              available: "active",
              pending: "pending",
              sold: "sold",
            };
            return {
              id: item.id,
              title: item.title,
              price: Number(item.price) || 0,
              currency: item.currency || "EUR",
              // any unknown status (including 'draft') is treated as 'active'
              status: statusMapBackToUi[item.status] || "active",
              category: categoryMap[item.category] || item.category || "bags",
              image: primaryImage?.file_url || "/static/img/bag-sample.jpg",
              escrowAmount,
              listingStatus,
              activeTransaction: item.transactions?.[0] || null,
            };
          }
        );
        setListings(items);
      } else {
        console.error("Failed to refresh listings:", itemsResult.error);
      }
    } catch (error) {
      console.error("Error refreshing listings:", error);
    } finally {
      setIsFetching(false);
    }
  };

  // Handle opening confirm transaction modal
  const handleOpenConfirmModal = (listing) => {
    setSelectedConfirmListing(listing);
    setConfirmModalOpen(true);
    setConfirmError("");
  };

  // Handle confirm transaction (seller confirms delivery) - called from modal
  const handleConfirmTransaction = async () => {
    if (!selectedConfirmListing?.listingStatus?.transactionId) {
      setConfirmError("Transaction ID is missing.");
      return;
    }

    setConfirmingTransaction(true);
    setConfirmError("");

    try {
      const res = await apiService.post(
        `/transactions/${selectedConfirmListing.listingStatus.transactionId}/confirm-seller`
      );
      if (res.success) {
        // Close modal first
        setConfirmModalOpen(false);
        setSelectedConfirmListing(null);

        // Wait a moment for backend to process, then refresh
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Refresh listings to get updated data
        await refreshListings();
      } else {
        setConfirmError(res.error || "Failed to confirm transaction.");
      }
    } catch (error) {
      setConfirmError("Failed to confirm transaction. Please try again.");
      console.error("Error confirming transaction:", error);
    } finally {
      setConfirmingTransaction(false);
    }
  };

  // Handle confirm funds reception (seller confirms they received money in bank)
  const handleConfirmFunds = async (transactionId) => {
    if (!transactionId) return;
    try {
      const res = await apiService.post(
        `/transactions/${transactionId}/confirm-funds`
      );
      if (res.success) {
        // Refresh listings to show updated status
        await refreshListings();
      } else {
        console.error("Failed to confirm funds:", res.error);
        alert(res.error || "Failed to confirm funds reception");
      }
    } catch (error) {
      console.error("Error confirming funds:", error);
      alert("Error confirming funds reception. Please try again.");
    }
  };

  return (
    <>
      <LayoutBuyerSeller>
        {/* Header band with subtle blue background */}
        <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
        <div className="relative mb-0 md:mb-8 z-[10]">
          <div className="flex px-5 items-center justify-between">
            <div>
              <h2 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
                My listings
              </h2>
              <p className="text-small-price opacity-50 mt-4 md:text-small-price text-white lg:text-[#1E1E1E] !font-normal">
                Find all of your listings below.
              </p>
            </div>
            <button
              onClick={() => setIsCreateOpen(true)}
              className="hidden md:inline-flex items-center gap-2 px-3 md:px-4 py-2.5 bg-blue-gradient text-white rounded-[8px] font-semibold text-sm"
            >
              ＋ Create new listing
            </button>
          </div>
          {/* Search / filters toolbar to mimic reference UI */}
          <div className="mt-8">
            <div className="w-full bg-[#F5F8FF] md:bg-white border-none md:border border-[#DCEDFF] rounded-tl-[25px] lg:rounded-tl-[12px] rounded-tr-[25px] lg:rounded-tr-[12px] rounded-br-[0px] lg:rounded-br-[12px] rounded-bl-[0px] lg:rounded-bl-[12px] p-5 pt-[40px] md:pt-5 grid md:flex items-center gap-4">
              <div className="relative flex-1">
                <button
                  onClick={() => setIsCreateOpen(true)}
                  className="inline-flex md:hidden items-center justify-center gap-2 px-3 mb-4 w-full md:px-4 py-2.5 bg-blue-gradient text-white rounded-[8px] font-semibold text-sm"
                >
                  ＋ Create new listing
                </button>
                <div className="relative flex-1">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full h-11 rounded-[8px] border border-gray-200 pl-9 pr-[12px] text-caption bg-white"
                    placeholder="Search listings..."
                  />
                  <HiSearch className="absolute left-3 top-3.5 text-gray-400 h-4 w-4" />
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-2.5 py-2.5 rounded-[8px] border border-gray-200 bg-white text-caption text-esdarkGray">
                <span aria-hidden>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                  >
                    <g opacity="0.5">
                      <path
                        d="M3.88589 5.66687C3.12955 4.82155 2.75138 4.39889 2.73712 4.03968C2.72473 3.72764 2.85882 3.42772 3.09963 3.22889C3.37684 3 3.94399 3 5.07828 3H19.9212C21.0555 3 21.6227 3 21.8999 3.22889C22.1407 3.42772 22.2748 3.72764 22.2624 4.03968C22.2481 4.39889 21.8699 4.82155 21.1136 5.66687L15.4074 12.0444C15.2566 12.2129 15.1812 12.2972 15.1275 12.3931C15.0798 12.4781 15.0448 12.5697 15.0236 12.6648C14.9997 12.7721 14.9997 12.8852 14.9997 13.1113V18.4584C14.9997 18.6539 14.9997 18.7517 14.9682 18.8363C14.9403 18.911 14.895 18.9779 14.836 19.0315C14.7692 19.0922 14.6784 19.1285 14.4969 19.2012L11.0969 20.5612C10.7293 20.7082 10.5455 20.7817 10.398 20.751C10.269 20.7242 10.1558 20.6476 10.083 20.5377C9.99975 20.4122 9.99975 20.2142 9.99975 19.8184V13.1113C9.99975 12.8852 9.99975 12.7721 9.97587 12.6648C9.95469 12.5697 9.91971 12.4781 9.87204 12.3931C9.81828 12.2972 9.7429 12.2129 9.59213 12.0444L3.88589 5.66687Z"
                        stroke="#1E1E1E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                  </svg>
                </span>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="bg-transparent w-full focus:outline-none text-[14px] text-[#1E1E1E]"
                >
                  <option value="all">All status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="sold">Sold</option>image.png
                </select>
              </div>
              <div className="hidden md:inline-flex items-center gap-2 px-2.5 py-2.5 rounded-[8px] border border-gray-200 bg-white text-caption text-esdarkGray">
                <span aria-hidden>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="21"
                    height="18"
                    viewBox="0 0 21 18"
                    fill="none"
                  >
                    <path
                      d="M15.5 1V17M15.5 17L11.5 13M15.5 17L19.5 13M5.5 17V1M5.5 1L1.5 5M5.5 1L9.5 5"
                      stroke="#1E1E1E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="bg-transparent w-full focus:outline-none text-[14px] text-[#1E1E1E]"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="hidden md:flex items-center gap-4 ml-auto">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`h-9 w-9 rounded-md border flex items-center justify-center ${
                    viewMode === "grid"
                      ? "bg-blue-gradient text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                  aria-label="Grid view"
                >
                  <IconGrid />
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={`h-9 w-9 rounded-md border flex items-center justify-center ${
                    viewMode === "table"
                      ? "bg-blue-gradient text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                  aria-label="Table view"
                >
                  <IconList />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Top-level loader while checking bank details / initial state */}
        {checkingBank && (
          <div className="bg-white border-none md:border border-[#DCEDFF] rounded-[12px] p-8 md:p-12 mx-5 md:mx-0 mt-4 text-center text-sm text-gray-500">
            Loading your listings...
          </div>
        )}

        {/* Bank details CTA if not configured */}
        {hasBankDetails === false && !checkingBank && (
          <div className="bg-white border-none md:border border-[#DCEDFF] rounded-[12px] p-8 md:p-12 mx-5 md:mx-0 text-center flex flex-col items-center justify-center">
            <div className="max-w-[500px] w-full mx-auto">
              <div className="h-10 mx-auto w-10 md:h-12 md:w-12 text-esblue flex items-center justify-center mb-8">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 48 48"
                  fill="none"
                >
                  <path
                    d="M28 4H12C10.9391 4 9.92172 4.42143 9.17157 5.17157C8.42143 5.92172 8 6.93913 8 8V40C8 41.0609 8.42143 42.0783 9.17157 42.8284C9.92172 43.5786 10.9391 44 12 44H36C37.0609 44 38.0783 43.5786 38.8284 42.8284C39.5786 42.0783 40 41.0609 40 40V16M28 4L40 16M28 4V16H40M24 36V24M18 30H30"
                    stroke="#2A8BF7"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-[20px] md:text-[24px] font-semibold text-[#1E1E1E]">
                Please add your bank details to create your first listing.
              </h3>
              <p className="mt-8 max-w-[520px] opacity-50 text-[12px] md:text-[16px] text-[#1E1E1E] ">
                Start building your luxury marketplace by creating your first
                listing. Showcase high-value items with secure escrow
                protection.
              </p>
              <Link
                to="/my-profile"
                className="mt-8 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-gradient w-full mx-auto max-w-[320px] text-white rounded-[8px] font-semibold text-sm"
              >
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="none"
                  className="shrink-0"
                >
                  <path
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.105c1.58-.91 3.356.866 2.447 2.446a1.724 1.724 0 0 0 1.105 2.574c1.756.425 1.756 2.923 0 3.348a1.724 1.724 0 0 0-1.105 2.574c.909 1.58-.867 3.356-2.447 2.446a1.724 1.724 0 0 0-2.573 1.105c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.105c-1.58.91-3.356-.866-2.447-2.446a1.724 1.724 0 0 0-1.105-2.574c-1.756-.425-1.756-2.923 0-3.348a1.724 1.724 0 0 0 1.105-2.574c-.909-1.58.867-3.356 2.447-2.446.948.546 2.147.142 2.573-1.105Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Add bank details</span>
              </Link>
            </div>
          </div>
        )}

        {/* Mobile cards (show on small screens only) */}
        {hasBankDetails === true && (
          <div className="md:hidden bg-[#F5F8FF] md:bg-white border-none md:border border-[#DCEDFF] rounded-[12px] p-4">
            {isFetching && (
              <div className="text-center py-6 text-sm text-gray-500">
                Loading items...
              </div>
            )}
            {fetchError && (
              <div className="text-center py-6 text-sm text-red-500">
                {fetchError}
              </div>
            )}
            {filtered.length === 0 ? (
              !isFetching && (
                <div className="text-center py-6 text-sm text-gray-500">
                  No data found
                </div>
              )
            ) : (
              <div className="space-y-4">
                {filtered.map((listing) => (
                  <div
                    key={listing.id}
                    className="bg-white border border-[#DCEDFF] rounded-[16px] p-3"
                  >
                    <div className="flex gap-3">
                      <div className="h-[56px] w-[56px] rounded-[8px] overflow-hidden bg-gray-100 shrink-0">
                        <img
                          src={process.env.REACT_APP_IMAGE_URL + listing.image}
                          alt={listing.title}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <h3 className="text-[13px] leading-4 font-medium text-[#1E1E1E] line-clamp-2 flex-1 min-w-0">
                            {listing.title}
                          </h3>
                          {listing.listingStatus && (
                            <span
                              className={`text-[11px] px-2 py-[3px] rounded-full capitalize whitespace-nowrap ${
                                listing.listingStatus.statusColor ||
                                "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {listing.listingStatus.statusText}
                            </span>
                          )}
                        </div>
                        {listing.listingStatus?.description && (
                          <p className="text-[10px] text-gray-500 mt-1">
                            {listing.listingStatus.description}
                          </p>
                        )}
                        <div className="mt-1 flex items-center gap-2 text-[12px] text-[#1E1E1E] opacity-70">
                          {listing.category !== "other" && (
                            <img
                              src={
                                CATEGORY_ICON_BY_KEY[listing.category] ||
                                bagIcon
                              }
                              alt="category"
                              className="h-4 w-4"
                            />
                          )}
                          <span className="capitalize">
                            {listing.category.replace("_", " ")}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="text-[#000025] font-semibold text-[18px] leading-5">
                        {listing.price.toFixed(2)} {listing.currency}
                      </div>
                      <div className="text-[11px] text-esblue mt-1">
                        {`${(listing.escrowAmount ?? 0).toFixed(2)} ${
                          listing.currency
                        } in escrow`}
                      </div>
                    </div>

                    <div className="mt-3 flex flex-col gap-2">
                      {listing.listingStatus?.action ===
                        "confirm_transaction" && (
                        <button
                          onClick={() => handleOpenConfirmModal(listing)}
                          className="w-full bg-[#B9FFCC] text-[#16653F] text-[13px] py-2 rounded-[8px] flex items-center justify-center gap-2"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                          {listing.listingStatus.actionText}
                        </button>
                      )}
                      {listing.listingStatus?.action === "confirm_funds" && (
                        <button
                          onClick={() =>
                            handleConfirmFunds(
                              listing.listingStatus.transactionId
                            )
                          }
                          className="w-full bg-green-500 text-white text-[13px] py-2 rounded-[8px] flex items-center justify-center gap-2"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                          {listing.listingStatus.actionText}
                        </button>
                      )}
                      {listing.listingStatus?.action === "see_offers" && (
                        <button
                          onClick={() => {
                            navigate("/seller/requests");
                          }}
                          className="w-full bg-[#B9F2FF] text-[#2A8BF7] text-[13px] py-2 rounded-[8px] flex items-center justify-center gap-2"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="22"
                            height="20"
                            viewBox="0 0 22 20"
                            fill="none"
                          >
                            <path
                              d="M18 19L21 16M21 16L18 13M21 16H15M14.5 1.29076C15.9659 1.88415 17 3.32131 17 5C17 6.67869 15.9659 8.11585 14.5 8.70924M11 13H7C5.13623 13 4.20435 13 3.46927 13.3045C2.48915 13.7105 1.71046 14.4892 1.30448 15.4693C1 16.2044 1 17.1362 1 19M12.5 5C12.5 7.20914 10.7091 9 8.5 9C6.29086 9 4.5 7.20914 4.5 5C4.5 2.79086 6.29086 1 8.5 1C10.7091 1 12.5 2.79086 12.5 5Z"
                              stroke="#2A8BF7"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {listing.listingStatus.actionText}
                        </button>
                      )}
                      {listing.listingStatus?.action === "send_offers" && (
                        <button
                          onClick={() => {
                            setSelectedTransactionItem(listing);
                            setSelectedItemId(listing.id);
                            setIsTransactionModalOpen(true);
                          }}
                          className="w-full border border-[#2A8BF7] bg-white text-esblue text-[13px] py-2 rounded-[8px] flex items-center justify-center gap-2"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <polyline points="22,6 12,13 2,6" />
                          </svg>
                          {listing.listingStatus.actionText}
                        </button>
                      )}
                      <button
                        onClick={() =>
                          navigate(`/seller/listings/${listing.id}`)
                        }
                        className="w-full bg-[#EAF4FF] text-esblue text-[13px] py-2 rounded-[8px] text-center"
                      >
                        View listing
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Desktop/Tablet Views */}
        {hasBankDetails === true && (
          <div className="hidden md:block">
            {viewMode === "grid" ? (
              <div className="bg-white border-none md:border border-[#DCEDFF] rounded-[12px] p-5">
                {/* Category chips merged with content */}
                <div className="mb-8 flex items-center gap-4 overflow-x-auto">
                  {CATEGORY_CHIPS.map((chip) => (
                    <button
                      key={chip.key}
                      onClick={() => setCategory(chip.key)}
                      className={`px-3 w-fit min-w-fit max-w-fit py-[8.5px] rounded-[8px] text-[14px] whitespace-nowrap transition flex items-center gap-4 border border-esBorderLightBlur ${
                        category === chip.key
                          ? "bg-esBorderLightBlur text-[#2A8BF7]"
                          : ""
                      }`}
                    >
                      {chip.icon && (
                        <img
                          src={chip.icon}
                          alt={chip.label}
                          className="h-6 w-6"
                        />
                      )}
                      {chip.label}
                      <span
                        className={`ml-1 rounded-[50px] px-[10px] py-1 text-[14px] ${
                          category === chip.key
                            ? "bg-white text-[#575757]"
                            : "bg-[#E0E0E0] text-[#575757]"
                        }`}
                      >
                        listed
                      </span>
                    </button>
                  ))}
                </div>
                {isFetching ? (
                  <div className="p-10 text-center text-sm text-gray-600">
                    Loading items...
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-10 text-center text-sm text-gray-600">
                    No data found
                  </div>
                ) : (
                  <div className="flex md:grid gap-4 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
                    {filtered.map((listing) => (
                      <div
                        key={listing.id}
                        className="bg-white w-full min-w-full border border-[#DCEDFF] rounded-[24px] overflow-hidden p-4"
                      >
                        <div className="relative h-44 bg-gray-100 rounded-[12px] overflow-hidden">
                          <img
                            src={
                              process.env.REACT_APP_IMAGE_URL + listing.image
                            }
                            alt={listing.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                          {listing.listingStatus && (
                            <span
                              className={`absolute left-3 top-3 text-[14px] font-semibold px-2.5 py-1 rounded-[50px] capitalize whitespace-nowrap leading-none ${
                                listing.listingStatus.statusColor ||
                                "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {listing.listingStatus.statusText}
                            </span>
                          )}
                          {/* {listing.listingStatus?.description && (
                            <span className="absolute left-3 top-12 text-[12px] text-white bg-black bg-opacity-70 px-2 py-1 rounded">
                              {listing.listingStatus.description}
                            </span>
                          )} */}
                          {listing.category !== "other" && (
                            <button className="absolute right-3 top-3 text-white op bg-[#1E1E1E40] rounded-[15px] h-[30px] w-[30px] flex items-center justify-center">
                              <img
                                src={
                                  CATEGORY_ICON_BY_KEY[listing.category] ||
                                  carIcon
                                }
                                alt={listing.category}
                                className="w-4 h-4 invert"
                              />
                            </button>
                          )}
                        </div>

                        <div className="px-0 pt-5 pb-8">
                          <h3 className="font-medium leading-5 line-clamp-2 text-gray-900 break-words">
                            {listing.title}
                          </h3>
                          <div className="mt-8 grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-esDarkBlue text-caption opacity-50">
                                Sale price
                              </div>
                              <div className="text-esblue font-semibold text-small-price-d">
                                {listing.price.toFixed(2)} {listing.currency}
                              </div>
                            </div>
                            {/* <div className="text-right">
                              <div className="text-esDarkBlue text-caption opacity-50">
                                In escrow
                              </div>
                              <div className="text-esblue font-semibold text-small-price-d">
                                {(listing.escrowAmount ?? 0).toFixed(2)}{" "}
                                {listing.currency}
                              </div>
                            </div> */}
                          </div>
                        </div>

                        <div className="px-0 pb-0 space-y-4">
                          {listing.listingStatus?.action ===
                            "confirm_transaction" && (
                            <button
                              onClick={() => handleOpenConfirmModal(listing)}
                              className="w-full bg-[#B9FFCC] text-[#16653F] text-caption-d py-3 rounded-[8px] inline-flex text-[14px] font-[600] items-center justify-center gap-2"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                              {listing.listingStatus.actionText}
                            </button>
                          )}
                          {listing.listingStatus?.action ===
                            "confirm_funds" && (
                            <button
                              onClick={() =>
                                handleConfirmFunds(
                                  listing.listingStatus.transactionId
                                )
                              }
                              className="w-full bg-[#B9FFCC] text-[#16653F] text-caption-d py-3 rounded-[8px] inline-flex text-[14px] font-[600] items-center justify-center gap-2"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                              {listing.listingStatus.actionText}
                            </button>
                          )}
                          {listing.listingStatus?.action === "see_offers" && (
                            <button
                              onClick={() => {
                                navigate("/seller/requests");
                              }}
                              className="w-full bg-[#B9F2FF] text-[#2A8BF7] text-caption-d py-3 rounded-[8px] inline-flex text-[14px] font-[600] items-center justify-center gap-2"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="20"
                                viewBox="0 0 22 20"
                                fill="none"
                              >
                                <path
                                  d="M18 19L21 16M21 16L18 13M21 16H15M14.5 1.29076C15.9659 1.88415 17 3.32131 17 5C17 6.67869 15.9659 8.11585 14.5 8.70924M11 13H7C5.13623 13 4.20435 13 3.46927 13.3045C2.48915 13.7105 1.71046 14.4892 1.30448 15.4693C1 16.2044 1 17.1362 1 19M12.5 5C12.5 7.20914 10.7091 9 8.5 9C6.29086 9 4.5 7.20914 4.5 5C4.5 2.79086 6.29086 1 8.5 1C10.7091 1 12.5 2.79086 12.5 5Z"
                                  stroke="#2A8BF7"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              {listing.listingStatus.actionText}
                            </button>
                          )}
                          {listing.listingStatus?.action === "completed" &&
                            listing.listingStatus.transactionId && (
                              <Link
                                to={`/seller/transactions/${listing.listingStatus.transactionId}/invoice`}
                                className="w-full border border-[#2A8BF7] px-4 py-3 rounded-[8px] text-[14px] text-[#2A8BF7] font-[600] flex items-center justify-center gap-2 bg-white hover:bg-blue-50"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="22"
                                  viewBox="0 0 18 22"
                                  fill="none"
                                >
                                  <path
                                    d="M11 1.26953V5.40007C11 5.96012 11 6.24015 11.109 6.45406C11.2049 6.64222 11.3578 6.7952 11.546 6.89108C11.7599 7.00007 12.0399 7.00007 12.6 7.00007H16.7305M11 16H5M13 12H5M17 8.98822V16.2C17 17.8802 17 18.7202 16.673 19.362C16.3854 19.9265 15.9265 20.3854 15.362 20.673C14.7202 21 13.8802 21 12.2 21H5.8C4.11984 21 3.27976 21 2.63803 20.673C2.07354 20.3854 1.6146 19.9265 1.32698 19.362C1 18.7202 1 17.8802 1 16.2V5.8C1 4.11984 1 3.27976 1.32698 2.63803C1.6146 2.07354 2.07354 1.6146 2.63803 1.32698C3.27976 1 4.11984 1 5.8 1H9.01178C9.74555 1 10.1124 1 10.4577 1.08289C10.7638 1.15638 11.0564 1.27759 11.3249 1.44208C11.6276 1.6276 11.887 1.88703 12.4059 2.40589L15.5941 5.59411C16.113 6.11297 16.3724 6.3724 16.5579 6.67515C16.7224 6.94356 16.8436 7.2362 16.9171 7.5423C17 7.88757 17 8.25445 17 8.98822Z"
                                    stroke="#2A8BF7"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                {listing.listingStatus.actionText}
                              </Link>
                            )}
                          {listing.listingStatus?.action === "send_offers" && (
                            <button
                              onClick={() => {
                                setSelectedTransactionItem(listing);
                                setSelectedItemId(listing.id);
                                setIsTransactionModalOpen(true);
                              }}
                              className="w-full border border-[#2A8BF7] bg-white text-esblue text-caption-d py-3 rounded-[8px] inline-flex text-[14px] font-[600] items-center justify-center gap-2"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                <polyline points="22,6 12,13 2,6" />
                              </svg>
                              {listing.listingStatus.actionText}
                            </button>
                          )}
                          <div className="bg-[#F5F8FF] rounded-[8px]">
                            <button
                              onClick={() =>
                                navigate(`/seller/listings/${listing.id}`)
                              }
                              className="block w-full text-center text-caption-d text-esblue py-3 text-[14px] font-[600]"
                            >
                              View listing
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] overflow-hidden">
                {/* Category chips merged above table */}
                <div className="p-5 pb-8 flex items-center gap-4 overflow-x-auto">
                  {CATEGORY_CHIPS.map((chip) => (
                    <button
                      key={chip.key}
                      onClick={() => setCategory(chip.key)}
                      className={`px-3 w-fit min-w-fit max-w-fit py-[8.5px] rounded-[8px] text-[14px] whitespace-nowrap transition flex items-center gap-4 border border-esBorderLightBlur ${
                        category === chip.key
                          ? "bg-esBorderLightBlur text-[#2A8BF7]"
                          : ""
                      }`}
                    >
                      {chip.icon && (
                        <img
                          src={chip.icon}
                          alt={chip.label}
                          className="h-6 w-6"
                        />
                      )}
                      {chip.label}
                      <span
                        className={`ml-1 rounded-[50px] px-[10px] py-1 text-[14px] ${
                          category === chip.key
                            ? "bg-white text-[#575757]"
                            : "bg-[#E0E0E0] text-[#575757]"
                        }`}
                      >
                        listed
                      </span>
                    </button>
                  ))}
                </div>
                <div className="w-full overflow-x-auto">
                  <table className="w-[97%] mx-auto min-w-[900px]">
                    <thead className="bg-[#1E1E1E0D] text-subsection-title-d text-[#1E1E1E]">
                      <tr>
                        <th className="text-left  min-w-[250px] py-3 px-5 rounded-tl-[12px]">
                          Item
                        </th>
                        <th className="text-left  min-w-[200px] py-3 px-5">
                          Type
                        </th>
                        <th className="text-left  min-w-[150px] py-3 px-5">
                          Price
                        </th>
                        <th className="text-left  min-w-[100px] py-3 px-5">
                          Status
                        </th>
                        <th className="text-left py-3 px-5 rounded-tr-[12px]">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr>
                          <td
                            colSpan="5"
                            className="text-center py-8 text-sm text-gray-600"
                          >
                            No data found
                          </td>
                        </tr>
                      ) : (
                        filtered.map((listing) => (
                          <tr
                            key={listing.id}
                            className="border-t border-gray-100"
                          >
                            <td className="py-3 px-5">
                              <div className="flex items-center gap-4">
                                <div className="h-10 w-10 bg-gray-100 rounded-[2.83px] overflow-hidden">
                                  <img
                                    src={
                                      process.env.REACT_APP_IMAGE_URL +
                                      listing.image
                                    }
                                    alt="thumb"
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                </div>
                                <div>
                                  <div className="font-semibold text-body-d line-clamp-1">
                                    {truncateText(`${listing.title}`)}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-5 capitalize">
                              <div className="flex gap-3 items-center">
                                {listing.category !== "other" && (
                                  <img
                                    src={
                                      CATEGORY_ICON_BY_KEY[listing.category] ||
                                      carIcon
                                    }
                                    alt={listing.category}
                                    className="h-6 w-6"
                                  />
                                )}
                                <div className="opacity-75 text-tag-d">
                                  {listing.category.replace("_", " ")}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-5 text-esDarkBlue text-page-subtitle-d font-bold">
                              <div>
                                {listing.price.toFixed(2)} {listing.currency}
                                {/* <div className="text-esblue text-tag font-medium">
                                  {`${(listing.escrowAmount ?? 0).toFixed(2)} ${
                                    listing.currency
                                  } in escrow`}
                                </div> */}
                              </div>
                            </td>
                            <td className="py-3 px-5">
                              <div className="flex flex-col gap-2">
                                {listing.listingStatus && (
                                  <>
                                    <span
                                      className={`text-[14px] font-semibold px-2.5 py-1 rounded-full capitalize whitespace-nowrap leading-none w-fit ${
                                        listing.listingStatus.statusColor ||
                                        "bg-gray-100 text-gray-700"
                                      }`}
                                    >
                                      {listing.listingStatus.statusText}
                                    </span>
                                    {listing.listingStatus.description && (
                                      <span className="text-[12px] text-gray-500">
                                        {listing.listingStatus.description}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-5">
                              <div className="flex items-center gap-4">
                                {listing.listingStatus?.action ===
                                  "confirm_transaction" && (
                                  <button
                                    onClick={() =>
                                      handleOpenConfirmModal(listing)
                                    }
                                    className="inline-flex items-center gap-2 bg-[#B9FFCC] text-[#16653F] text-subsection-title py-3 px-3 rounded-[8px]"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M20 6L9 17l-5-5" />
                                    </svg>
                                    {listing.listingStatus.actionText}
                                  </button>
                                )}
                                {listing.listingStatus?.action ===
                                  "confirm_funds" && (
                                  <button
                                    onClick={() =>
                                      handleConfirmFunds(
                                        listing.listingStatus.transactionId
                                      )
                                    }
                                    className="inline-flex items-center gap-2 bg-green-500 text-white text-subsection-title py-3 px-3 rounded-[8px]"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M20 6L9 17l-5-5" />
                                    </svg>
                                    {listing.listingStatus.actionText}
                                  </button>
                                )}
                                {listing.listingStatus?.action ===
                                  "see_offers" && (
                                  <button
                                    onClick={() => {
                                      navigate("/seller/requests");
                                    }}
                                    className="inline-flex items-center gap-2 bg-[#B9F2FF] text-[#2A8BF7] text-subsection-title py-3 px-3 rounded-[8px]"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="22"
                                      height="20"
                                      viewBox="0 0 22 20"
                                      fill="none"
                                    >
                                      <path
                                        d="M18 19L21 16M21 16L18 13M21 16H15M14.5 1.29076C15.9659 1.88415 17 3.32131 17 5C17 6.67869 15.9659 8.11585 14.5 8.70924M11 13H7C5.13623 13 4.20435 13 3.46927 13.3045C2.48915 13.7105 1.71046 14.4892 1.30448 15.4693C1 16.2044 1 17.1362 1 19M12.5 5C12.5 7.20914 10.7091 9 8.5 9C6.29086 9 4.5 7.20914 4.5 5C4.5 2.79086 6.29086 1 8.5 1C10.7091 1 12.5 2.79086 12.5 5Z"
                                        stroke="#2A8BF7"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    {listing.listingStatus.actionText}
                                  </button>
                                )}
                                {listing.listingStatus?.action ===
                                  "send_offers" && (
                                  <button
                                    onClick={() => {
                                      setSelectedTransactionItem(listing);
                                      setSelectedItemId(listing.id);
                                      setIsTransactionModalOpen(true);
                                    }}
                                    className="inline-flex items-center gap-2 border border-[#2A8BF7] bg-white text-esblue text-subsection-title py-3 px-3 rounded-[8px]"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                      <polyline points="22,6 12,13 2,6" />
                                    </svg>
                                    {listing.listingStatus.actionText}
                                  </button>
                                )}
                                {listing.listingStatus?.action ===
                                  "completed" && (
                                  <button className="border border-[#2A8BF7] px-4 py-3 rounded-[8px] text-[14px] text-[#2A8BF7] font-[600] flex items-center justify-center gap-2">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="22"
                                      viewBox="0 0 18 22"
                                      fill="none"
                                    >
                                      <path
                                        d="M11 1.26953V5.40007C11 5.96012 11 6.24015 11.109 6.45406C11.2049 6.64222 11.3578 6.7952 11.546 6.89108C11.7599 7.00007 12.0399 7.00007 12.6 7.00007H16.7305M11 16H5M13 12H5M17 8.98822V16.2C17 17.8802 17 18.7202 16.673 19.362C16.3854 19.9265 15.9265 20.3854 15.362 20.673C14.7202 21 13.8802 21 12.2 21H5.8C4.11984 21 3.27976 21 2.63803 20.673C2.07354 20.3854 1.6146 19.9265 1.32698 19.362C1 18.7202 1 17.8802 1 16.2V5.8C1 4.11984 1 3.27976 1.32698 2.63803C1.6146 2.07354 2.07354 1.6146 2.63803 1.32698C3.27976 1 4.11984 1 5.8 1H9.01178C9.74555 1 10.1124 1 10.4577 1.08289C10.7638 1.15638 11.0564 1.27759 11.3249 1.44208C11.6276 1.6276 11.887 1.88703 12.4059 2.40589L15.5941 5.59411C16.113 6.11297 16.3724 6.3724 16.5579 6.67515C16.7224 6.94356 16.8436 7.2362 16.9171 7.5423C17 7.88757 17 8.25445 17 8.98822Z"
                                        stroke="#2A8BF7"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                      />
                                    </svg>
                                    {listing.listingStatus.actionText}
                                  </button>
                                )}
                                <button
                                  onClick={() =>
                                    navigate(`/seller/listings/${listing.id}`)
                                  }
                                  className="inline-flex items-center gap-4 bg-eslightBlue text-esblue text-subsection-title py-3 px-3 rounded-[8px]"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M2.42012 12.7132C2.28394 12.4975 2.21584 12.3897 2.17772 12.2234C2.14909 12.0985 2.14909 11.9015 2.17772 11.7766C2.21584 11.6103 2.28394 11.5025 2.42012 11.2868C3.54553 9.50484 6.8954 5 12.0004 5C17.1054 5 20.4553 9.50484 21.5807 11.2868C21.7169 11.5025 21.785 11.6103 21.8231 11.7766C21.8517 11.9015 21.8517 12.0985 21.8231 12.2234C21.785 12.3897 21.7169 12.4975 21.5807 12.7132C20.4553 14.4952 17.1054 19 12.0004 19C6.8954 19 3.54553 14.4952 2.42012 12.7132Z"
                                      stroke="#2A8BF7"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M12.0004 15C13.6573 15 15.0004 13.6569 15.0004 12C15.0004 10.3431 13.6573 9 12.0004 9C10.3435 9 9.0004 10.3431 9.0004 12C9.0004 13.6569 10.3435 15 12.0004 15Z"
                                      stroke="#2A8BF7"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>{" "}
                                  View listing
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </LayoutBuyerSeller>
      <CreateListingModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSave={handleCreateListing}
        isLoading={isLoading}
        error={error}
      />
      <TransactionManagementModal
        open={isTransactionModalOpen}
        onClose={() => setIsTransactionModalOpen(false)}
        transactionData={selectedTransactionItem}
        itemId={selectedItemId}
        onInviteSent={refreshListings}
      />
      <ConfirmTransactionDoneModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setSelectedConfirmListing(null);
          setConfirmError("");
        }}
        onConfirm={handleConfirmTransaction}
        isLoading={confirmingTransaction}
        errorText={confirmError}
      />
    </>
  );
};

export default SellerMylistings;
